// Lead Calls tab — full Plivo browser-softphone experience:
//  • Dialer (PSTN E.164 with default country) + Test mode toggle
//  • Live call history (10s polling) with status pill, duration, mode badge
//  • Recording playback (authenticated blob fetch) + download
//  • AI summary (server-generated post-recording) + transcript expander
//  • Inline note panel + delete

import React, { useEffect, useRef, useState } from "react";
import {
  PhoneCall, PhoneOff, Loader2, Play, Download, Globe, Smartphone,
  Sparkles, ChevronDown, ChevronUp, Clock, Trash2, FileText, AlertCircle,
  Beaker, ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button, IconButton, Input, Select, Textarea } from "../../atoms";
import {
  useCallLogs, useInitiateCall, useDeleteCall, useRegenerateSummary,
  useUpdateCallNote, fetchRecordingBlobUrl,
  useLeadCallsRollup, useGenerateLeadCallsRollup,
} from "../../hooks/useLeads";
import { useActiveCall } from "../../contexts/ActiveCallProvider";
import useFocusRow, { FOCUS_ROW_STYLE } from "../../hooks/useFocusRow";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";

const STATUS_TONE = {
  completed:    { bg: "var(--crm-success-soft)", fg: "var(--crm-success)" },
  "in-progress":{ bg: "var(--crm-primary-soft)", fg: "var(--crm-primary)" },
  ringing:      { bg: "var(--crm-primary-soft)", fg: "var(--crm-primary)" },
  queued:       { bg: "var(--color-bg-muted)",   fg: "var(--color-text-secondary)" },
  initiated:    { bg: "var(--color-bg-muted)",   fg: "var(--color-text-secondary)" },
  failed:       { bg: "var(--crm-danger-soft)",  fg: "var(--crm-danger)" },
  "no-answer":  { bg: "var(--crm-warning-soft, var(--color-bg-muted))", fg: "var(--crm-warning, var(--color-text-secondary))" },
  busy:         { bg: "var(--crm-warning-soft, var(--color-bg-muted))", fg: "var(--crm-warning, var(--color-text-secondary))" },
  canceled:     { bg: "var(--color-bg-muted)",   fg: "var(--color-text-secondary)" },
};

function formatDuration(seconds) {
  const n = Number(seconds) || 0;
  if (n <= 0) return "0:00";
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function formatDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Request failed";
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.initiated;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 8px", borderRadius: 999,
      fontSize: 10, fontWeight: 600, background: tone.bg, color: tone.fg,
    }}>{status}</span>
  );
}

function ModeBadge({ mode }) {
  const Icon = mode === "browser" ? Globe : Smartphone;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)",
    }}>
      <Icon size={11} /> {mode}
    </span>
  );
}

function Dialer({ leadId, lead, onStarted }) {
  const initiate = useInitiateCall(leadId);
  const { startCall, activeCall } = useActiveCall();
  const [phone, setPhone] = useState(lead?.phone || "");
  const [country, setCountry] = useState("IN");
  // eslint-disable-next-line no-unused-vars -- setTestMode is used by the (currently commented) test-endpoint toggle in the header
  const [testMode, setTestMode] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setPhone(lead?.phone || ""); }, [lead?.phone]);

  const submit = async () => {
    setError(null);
    if (!testMode && !phone.trim()) {
      setError("Enter a phone number to call");
      return;
    }
    if (activeCall) {
      toast.error("A call is already in progress. End it before starting another.");
      return;
    }
    try {
      const result = await initiate.mutateAsync({
        to: phone.trim(),
        mode: "browser",
        defaultCountry: country,
        testMode,
      });
      startCall({
        leadId,
        callLogId: result.callLogId,
        to: result.dialTarget || result.to,
        leadTitle: lead?.title || "Lead",
        testMode,
      });
      onStarted?.();
    } catch (err) {
      setError(formatErr(err));
    }
  };

  const dialCode = country === "US" ? "+1" : "+91";
  const digits = phone.replace(/[^\d]/g, "");
  const dialPreview = digits ? `${dialCode} ${digits}` : null;
  const canCall = !initiate.isPending && !activeCall && (testMode || !!phone.trim());

  const onKeyDown = (e) => {
    if (e.key === "Enter" && canCall) { e.preventDefault(); submit(); }
  };

  return (
    <div style={{
      border: "1px solid var(--color-border-subtle)",
      background: "var(--color-bg-surface)",
      borderRadius: 8, padding: 16,
      display: "flex", flexDirection: "column", gap: 14,
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    }}>
      {/* Header: icon + title on the left, test toggle on the right */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 30, height: 30, borderRadius: 9,
            background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
          }}>
            <PhoneCall size={15} />
          </span>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--color-text-primary)", lineHeight: 1.2 }}>Start a call</div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {testMode ? "Test endpoint mode" : "Browser softphone · Plivo"}
            </div>
          </div>
        </div>
        {/* <label style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontSize: 11, fontWeight: 600, padding: "5px 9px", borderRadius: 8,
          color: testMode ? "var(--crm-primary)" : "var(--color-text-secondary)",
          background: testMode ? "var(--crm-primary-soft)" : "var(--color-bg-muted)",
          border: `1px solid ${testMode ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
          cursor: "pointer", userSelect: "none", transition: "all 120ms ease",
        }}>
          <input
            type="checkbox"
            checked={testMode}
            onChange={(e) => setTestMode(e.target.checked)}
            style={{ accentColor: "var(--crm-primary)", cursor: "pointer" }}
          />
          <Beaker size={12} /> Test endpoint
        </label> */}
      </div>

      {!testMode && (
        <div>
          {/* Joined country+number group, then Call — kept tight (no full-width stretch) */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{
              display: "flex", alignItems: "center", flex: "1 1 360px", maxWidth: 440,
              border: "1px solid var(--color-border-subtle)", borderRadius: 8,
              background: "var(--color-bg-surface)", overflow: "hidden",
            }}>
              <div style={{ width: 118, flexShrink: 0, borderRight: "1px solid var(--color-border-subtle)" }}>
                <Select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  aria-label="Default country"
                  variant="borderless"
                  style={{ height: 38 }}
                >
                  <option value="IN">🇮🇳 IN (+91)</option>
                  <option value="US">🇺🇸 US (+1)</option>
                </Select>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="98765 43210"
                autoComplete="tel"
                style={{
                  flex: 1, minWidth: 0, height: 38, padding: "0 12px",
                  border: "none", outline: "none", background: "transparent",
                  color: "var(--color-text-primary)", fontSize: 14, fontFamily: "inherit",
                }}
              />
            </div>
            <Button
              variant="primary"
              size="md"
              onClick={submit}
              disabled={!canCall}
              style={{ flexShrink: 0 }}
              leftIcon={initiate.isPending ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />}
            >
              {initiate.isPending ? "Calling…" : activeCall ? "In call…" : "Call"}
            </Button>
          </div>
          <div style={{ minHeight: 18, marginTop: 8 }}>
            {error ? (
              <div style={{ fontSize: 12, color: "var(--crm-danger)", display: "flex", alignItems: "center", gap: 6 }}>
                <AlertCircle size={12} /> {error}
              </div>
            ) : dialPreview ? (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 5 }}>
                <Globe size={11} /> Will dial <span style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>{dialPreview}</span>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--color-text-tertiary, var(--color-text-secondary))" }}>
                Enter a number and press Enter to call.
              </div>
            )}
          </div>
        </div>
      )}

      {testMode && (
        <div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: "18px", padding: "10px 12px", background: "var(--color-bg-muted)", borderRadius: 8 }}>
            Dials the configured test SIP endpoint instead of a real number — useful for end-to-end
            verification without burning Plivo credit.
          </div>
          {error && (
            <div style={{ fontSize: 12, color: "var(--crm-danger)", marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertCircle size={12} /> {error}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <Button
              variant="primary"
              size="md"
              onClick={submit}
              disabled={!canCall}
              leftIcon={initiate.isPending ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />}
            >
              {initiate.isPending ? "Calling…" : activeCall ? "In call…" : "Call test endpoint"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordingPlayer({ leadId, callId }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetchRecordingBlobUrl(leadId, callId)
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        urlRef.current = url;
        setBlobUrl(url);
      })
      .catch((err) => { if (!cancelled) setError(formatErr(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [leadId, callId]);

  if (loading) return (
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
      <Loader2 size={12} className="animate-spin" /> Loading recording…
    </div>
  );
  if (error) return <div style={{ fontSize: 12, color: "var(--crm-danger)" }}>{error}</div>;
  if (!blobUrl) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <audio controls src={blobUrl} style={{ height: 32, maxWidth: "100%" }} />
      <a
        href={blobUrl}
        download={`call-${callId}.mp3`}
        style={{
          fontSize: 11, fontWeight: 600, color: "var(--crm-primary)",
          textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4,
        }}
      >
        <Download size={12} /> Download
      </a>
    </div>
  );
}

function SummarySection({ leadId, call }) {
  const [open, setOpen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const regen = useRegenerateSummary(leadId);

  const hasSummary = !!call.summary;
  const hasRecording = !!call.recordingUrl;
  const hasError = !!call.summaryError;
  // Server kicks off summary as soon as recording arrives. We show "Summarizing…"
  // for the first 5 min after recording-ready, BUT cut it short the moment
  // summaryError is set (e.g. transcription returned empty) so the user can
  // retry instead of staring at a stuck spinner.
  const recordingAgeMin = hasRecording
    ? (Date.now() - new Date(call.updatedAt || call.endedAt || call.createdAt).getTime()) / 60_000
    : 0;
  const autoPending = !hasSummary && !hasError && hasRecording && recordingAgeMin < 5;

  const onClick = async () => {
    if (hasSummary) { setOpen((o) => !o); return; }
    try { await regen.mutateAsync(call._id); toast.success("Generating summary…"); }
    catch (err) { toast.error(formatErr(err)); }
  };

  if (!hasRecording) return null;

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--color-border-subtle)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button
          onClick={onClick}
          disabled={regen.isPending || autoPending}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 10px", borderRadius: 8, border: "none",
            background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
            fontSize: 11, fontWeight: 600, cursor: regen.isPending || autoPending ? "default" : "pointer",
            opacity: regen.isPending || autoPending ? 0.6 : 1,
          }}
        >
          {regen.isPending || autoPending ? (
            <><Loader2 size={11} className="animate-spin" /> Summarizing…</>
          ) : hasSummary ? (
            <><Sparkles size={11} /> {open ? "Hide summary" : "View summary"}
              {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}</>
          ) : hasError ? (
            <><Sparkles size={11} /> Retry summary</>
          ) : (
            <><Sparkles size={11} /> Summarize with AI</>
          )}
        </button>
        {hasSummary && !regen.isPending && (
          <button
            onClick={async () => {
              try { await regen.mutateAsync(call._id); toast.success("Re-summarizing…"); }
              catch (err) { toast.error(formatErr(err)); }
            }}
            style={{
              border: "none", background: "transparent",
              fontSize: 11, color: "var(--color-text-secondary)", cursor: "pointer",
            }}
            title="Re-generate summary"
          >
            Re-summarize
          </button>
        )}
      </div>

      {call.summaryError && (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--crm-danger)" }}>{call.summaryError}</div>
      )}

      {open && hasSummary && (
        <div style={{
          marginTop: 10, padding: 12, borderRadius: 8,
          background: "var(--crm-primary-soft)",
          border: "1px solid var(--color-border-subtle)",
        }}>
          <div style={{ fontSize: 12, lineHeight: "19px", whiteSpace: "pre-wrap", color: "var(--color-text-primary)" }}>
            {call.summary}
          </div>
          {call.summaryGeneratedAt && (
            <div style={{ marginTop: 8, fontSize: 10, color: "var(--color-text-secondary)" }}>
              Generated {formatDateTime(call.summaryGeneratedAt)}
            </div>
          )}
          {call.transcript && (
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => setShowTranscript((s) => !s)}
                style={{
                  border: "none", background: "transparent", padding: 0,
                  fontSize: 11, fontWeight: 600, color: "var(--crm-primary)",
                  display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
                }}
              >
                {showTranscript ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                {showTranscript ? "Hide transcript" : "Show full transcript"}
              </button>
              {showTranscript && (
                <pre style={{
                  marginTop: 8, padding: 10, borderRadius: 8,
                  background: "var(--color-bg-base)",
                  fontSize: 11, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  whiteSpace: "pre-wrap", maxHeight: 256, overflowY: "auto",
                  color: "var(--color-text-secondary)",
                }}>
                  {call.transcript}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NoteEditor({ leadId, call, onDone }) {
  const update = useUpdateCallNote(leadId);
  const [title, setTitle] = useState(call.note?.title || "");
  const [description, setDescription] = useState(call.note?.description || "");
  const [error, setError] = useState(null);

  const save = async () => {
    setError(null);
    if (!description.trim()) { setError("Add a description"); return; }
    try {
      await update.mutateAsync({ callId: call._id, title: title.trim() || undefined, description: description.trim() });
      toast.success("Saved");
      onDone?.();
    } catch (err) { setError(formatErr(err)); }
  };

  return (
    <div style={{
      marginTop: 10, padding: 12, borderRadius: 8,
      background: "var(--color-bg-base)",
      border: "1px solid var(--color-border-subtle)",
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What was discussed? Next steps, action items…"
        rows={3}
        style={{ minHeight: 72 }}
      />
      {error && <div style={{ fontSize: 12, color: "var(--crm-danger)" }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
        <Button variant="secondary" size="sm" onClick={onDone}>Cancel</Button>
        <Button
          variant="primary" size="sm" onClick={save}
          disabled={update.isPending}
          leftIcon={update.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
        >
          Save note
        </Button>
      </div>
    </div>
  );
}

function CallRow({ leadId, call, onDelete, rowRef }) {
  const [open, setOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const hasRecording = !!call.recordingUrl;
  const isCompleted = call.status === "completed";
  const errored = ["failed", "no-answer", "busy", "canceled"].includes(call.status);

  return (
    <li ref={rowRef} style={{
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8,
      background: "var(--color-bg-surface)",
      overflow: "hidden",
    }}>
      <div style={{ padding: 14, display: "flex", gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9999, flexShrink: 0,
          background: errored ? "var(--crm-danger-soft)" : "var(--crm-primary-soft)",
          color: errored ? "var(--crm-danger)" : "var(--crm-primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {errored ? <PhoneOff size={16} /> : <PhoneCall size={16} />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {call.toNumber}
            </span>
            <StatusPill status={call.status} />
            {call.mode && <ModeBadge mode={call.mode} />}
            {call.testMode && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                padding: "1px 6px", borderRadius: 999,
                fontSize: 9, fontWeight: 600, background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
              }}>
                <Beaker size={9} /> Test
              </span>
            )}
          </div>
          <div style={{ marginTop: 3, fontSize: 11, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>{formatDateTime(call.createdAt)}</span>
            {(() => {
              const a = call.agent;
              const fullName = a && [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
              const who = fullName || a?.email || call.agentEmail;
              return who ? <span>· {who}</span> : null;
            })()}
            {call.duration > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                <Clock size={11} /> {formatDuration(call.duration)}
              </span>
            )}
          </div>
          {call.errorMessage && (
            <div style={{ marginTop: 4, fontSize: 11, color: "var(--crm-danger)" }}>{call.errorMessage}</div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {hasRecording && (
            <button
              onClick={() => setOpen((o) => !o)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "5px 10px", borderRadius: 8, border: "none",
                background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}
            >
              <Play size={11} /> {open ? "Hide" : "Play"}
            </button>
          )}
          <IconButton
            size="sm"
            aria-label="Delete call"
            onClick={onDelete}
          >
            <Trash2 size={13} />
          </IconButton>
        </div>
      </div>

      {(call.note?.description || editingNote) && (
        <div style={{ padding: "0 14px 12px" }}>
          {!editingNote ? (
            <div style={{
              padding: 10, borderRadius: 8,
              background: "var(--color-bg-muted)",
              fontSize: 12, lineHeight: "18px", color: "var(--color-text-primary)",
              whiteSpace: "pre-wrap",
            }}>
              {call.note?.title && (
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{call.note.title}</div>
              )}
              {call.note?.description}
              {isCompleted && (
                <button
                  onClick={() => setEditingNote(true)}
                  style={{
                    marginLeft: 6, border: "none", background: "transparent",
                    fontSize: 11, fontWeight: 600, color: "var(--crm-primary)", cursor: "pointer", padding: 0,
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          ) : (
            <NoteEditor leadId={leadId} call={call} onDone={() => setEditingNote(false)} />
          )}
        </div>
      )}

      {isCompleted && !call.note?.description && !editingNote && (
        <div style={{ padding: "0 14px 12px" }}>
          <button
            onClick={() => setEditingNote(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              border: "none", background: "transparent", padding: 0,
              fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", cursor: "pointer",
            }}
          >
            <FileText size={11} /> Add note <ChevronRight size={11} />
          </button>
        </div>
      )}

      {open && hasRecording && (
        <div style={{ padding: "0 14px 14px" }}>
          <RecordingPlayer leadId={leadId} callId={call._id} />
        </div>
      )}

      {hasRecording && (
        <div style={{ padding: "0 14px 14px" }}>
          <SummarySection leadId={leadId} call={call} />
        </div>
      )}

      {!hasRecording && isCompleted && (
        <div style={{ padding: "0 14px 12px", fontSize: 11, color: "var(--color-text-secondary)" }}>
          Recording processing…
        </div>
      )}
    </li>
  );
}

// Master "all-calls" summary card. Sits above the call list and lets the user
// generate / refresh a single overview of every call on the lead. Shown only
// once at least one call exists with a usable summary or transcript.
function RollupSummaryCard({ leadId, calls }) {
  const { data } = useLeadCallsRollup(leadId);
  const gen = useGenerateLeadCallsRollup(leadId);
  const [open, setOpen] = useState(false);

  // Coerce-guard: only trim real strings so an odd value can't crash the tab.
  const hasText = (v) => typeof v === "string" && v.trim().length > 0;
  const hasUsableCalls = calls.some((c) => hasText(c.summary) || hasText(c.transcript));
  if (calls.length === 0) return null;

  const summary = data?.summary;
  const generatedAt = data?.generatedAt;
  const callsIncluded = data?.callsIncluded || 0;
  const cachedError = data?.error;
  const hasSummary = !!summary;
  // If new calls came in after the rollup was generated, surface that so the
  // user knows clicking will pull in the latest ones.
  const stale = hasSummary && callsIncluded < calls.length;

  const onGenerate = async () => {
    try {
      await gen.mutateAsync();
      setOpen(true);
      toast.success("Master summary ready");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div style={{
      background: "var(--crm-primary-soft)",
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8, padding: 14,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Sparkles size={16} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
              Master summary — all calls
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {hasSummary ? (
                <>Based on {callsIncluded} call{callsIncluded === 1 ? "" : "s"} · generated {formatDateTime(generatedAt)}{stale ? ` · ${calls.length - callsIncluded} newer call(s) since` : ""}</>
              ) : !hasUsableCalls ? (
                <>Summarize individual calls first, then come back here.</>
              ) : (
                <>One click to combine all {calls.length} calls into a single overview.</>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {hasSummary && (
            <button
              onClick={() => setOpen((o) => !o)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "6px 10px", borderRadius: 8, border: "none",
                background: "transparent", color: "var(--crm-primary)",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}
            >
              {open ? <><ChevronUp size={11} /> Hide</> : <><ChevronDown size={11} /> View</>}
            </button>
          )}
          <button
            onClick={onGenerate}
            disabled={gen.isPending || !hasUsableCalls}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "6px 12px", borderRadius: 8, border: "none",
              background: "var(--crm-primary)", color: "#fff",
              fontSize: 11, fontWeight: 600,
              cursor: gen.isPending || !hasUsableCalls ? "not-allowed" : "pointer",
              opacity: gen.isPending || !hasUsableCalls ? 0.6 : 1,
            }}
            title={!hasUsableCalls ? "At least one call needs a summary or transcript first" : ""}
          >
            {gen.isPending
              ? <><Loader2 size={11} className="animate-spin" /> Summarizing…</>
              : hasSummary
                ? <><Sparkles size={11} /> {stale ? "Refresh" : "Re-generate"}</>
                : <><Sparkles size={11} /> Summarize all calls</>}
          </button>
        </div>
      </div>

      {cachedError && !gen.isPending && (
        <div style={{ fontSize: 11, color: "var(--crm-danger)" }}>{cachedError}</div>
      )}

      {open && hasSummary && (
        <div style={{
          padding: 12, borderRadius: 8,
          background: "var(--color-bg-surface)",
          border: "1px solid var(--color-border-subtle)",
          fontSize: 12, lineHeight: "20px", whiteSpace: "pre-wrap",
          color: "var(--color-text-primary)",
        }}>
          {summary}
        </div>
      )}
    </div>
  );
}

export default function CallsPanel({ leadId, lead, focusItemId, onFocusResolved }) {
  const { data, isLoading } = useCallLogs(leadId);
  const del = useDeleteCall(leadId);
  const calls = Array.isArray(data?.calls) ? data.calls : (Array.isArray(data) ? data : []);
  const [confirmId, setConfirmId] = useState(null);
  const { register } = useFocusRow(focusItemId, { onResolved: onFocusResolved });

  const confirmCall = confirmId ? calls.find((c) => c._id === confirmId) : null;
  const confirmLabel = confirmCall?.toNumber ? `Call to ${confirmCall.toNumber}` : "this call log";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{FOCUS_ROW_STYLE}</style>
      <Dialer leadId={leadId} lead={lead} />

      <RollupSummaryCard leadId={leadId} calls={calls} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          {isLoading ? "Loading…" : (
            calls.length === 0 ? "No calls yet" : `${calls.length} ${calls.length === 1 ? "call" : "calls"}`
          )}
        </div>
      </div>

      {!isLoading && calls.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--color-text-secondary)" }}>
          <PhoneCall size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)" }}>No calls yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Use the dialer above to place your first call.</div>
        </div>
      )}

      {calls.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {calls.map((c) => (
            <CallRow
              key={c._id}
              leadId={leadId}
              call={c}
              onDelete={() => setConfirmId(c._id)}
              rowRef={register(c._id)}
            />
          ))}
        </ul>
      )}
      <ConfirmDeleteModal
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => {
          if (!confirmId) return;
          del.mutate(confirmId, {
            onSuccess: () => setConfirmId(null),
          });
        }}
        title="Delete call log?"
        message={`${confirmLabel} will be removed. The recording (if any) stays on Plivo's servers.`}
        confirmLabel="Delete call"
        isPending={del.isPending}
      />
    </div>
  );
}
